import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { getCurrentUser, logout as authLogout } from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = useCallback((userData) => {
    if (userData) {
      // Ensure the user data is properly stored
      setUser(userData);
      // Enforce SUPER_ADMIN status if needed
      if (userData.role === "SUPER_ADMIN" && userData.status !== "active") {
        userData.status = "active";
        sessionStorage.setItem("user_profile", JSON.stringify(userData));
      }
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Clear user state first
      setUser(null);

      // Clear session storage directly to ensure logout works
      sessionStorage.clear();

      // Call the API logout
      await authLogout();

      // Redirect to login page using direct window location for reliability
      window.location.href = "/sign-in";
    } catch (error) {
      console.error("Logout error:", error);
      // Force logout even if API call fails
      setUser(null);
      sessionStorage.clear();
      window.location.href = "/sign-in";
    }
  }, []);

  const isSuperAdmin = useCallback(() => {
    return user?.role === "SUPER_ADMIN";
  }, [user?.role]);

  if (loading) {
    return null; // or a loading spinner
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isSuperAdmin,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
